import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'sk';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    overview: "Overview & Trading",
    tools: "Advanced Tools",
    settings: "Settings & Orders",
    admin: "Administration",
    logout: "Logout",
    statsValuation: "Current Assets Value",
    statsInvested: "Invested Capital",
    statsNetProfit: "Actual Yield",
    statsRealized: "Realized",
    statsSharpe: "Sharpe Ratio",
    statsSharpeDesc: "Risk-adjusted yield",
    statsVar: "Value at Risk (VaR 95%)",
    statsMaxDd: "Max Drawdown",
    weightTitle: "Asset Allocation Weight Explorer",
    weightDesc: "Click on an asset to focus quantitative risk models and projections",
    unitsAt: "units @",
    loadingMetrics: "Loading analytics metrics...",
    // holdings table
    holdingsTitle: "Active Holdings",
    colAsset: "Asset",
    colQty: "Quantity",
    colAvgCost: "Avg Cost",
    colPrice: "Market Price",
    colVal: "Valuation",
    colGainLoss: "Total Gain/Loss",
    noHoldings: "No active holdings. Build a position to start tracking.",
    // Newly added
    liveFeed: "Live Feed",
    offline: "Offline",
    explanationBtn: "App Manual & Setup",
    explanationTitle: "Anogi Traders Manual & Setup Guide",
    explanationDesc: "System feature guide and native iOS/Android installation walkthrough",
    closeBtn: "Close",
    understoodBtn: "Understood",
    viewPie: "Pie Chart",
    viewPieByAsset: "By Asset",
    viewPieBySector: "By Sector",
    // 5 Dashboard components
    rebalanceTitle: "⚖️ Rebalancing Advisor",
    rebalanceDesc: "Actionable trade recommendations to restore target weight distribution",
    rebalanceEmpty: "✓ Portfolio is balanced. All active holdings are within 1.5% of target allocation weights.",
    rebalanceUnits: "units",
    frontierTitle: "📈 Efficient Frontier Plotter",
    frontierDesc: "Modern Portfolio Theory: Risk (Volatility) vs. Expected Return Profile",
    frontierPortfolio: "PORTFOLIO",
    harvestTitle: "💸 Tax-Loss Harvesting Tracker",
    harvestDesc: "Scan capital loss positions to offset taxation liabilities (Slovak 19% rate estimated)",
    harvestEmpty: "✓ No harvesting opportunities found. All current positions are in profit or holding minimal losses.",
    taxCreditLabel: "Estimated Available Tax Credit:",
    harvestDetail: "units bought",
    harvestLoss: "Loss",
    harvestTaxSave: "Tax Save",
    stressTitle: "🌪️ Macro Stress-Test Simulator",
    stressDesc: "Simulated returns under macro stress factors based on weighted asset sensitivities",
    stressRateSpike: "Interest Rate Spike (+100bps)",
    stressRateSpikeDesc: "Rapid central bank rate hikes raising cost of debt capital.",
    stressInflation: "Inflation Shock (+3% Core)",
    stressInflationDesc: "Commodities and input price inflation surge.",
    stressPanic: "Global Financial Panic (VIX 40+)",
    stressPanicDesc: "Systemic risk-off asset liquidation across equity clusters.",
    sentimentTitle: "📰 Sentiment Index & News Signal",
    sentimentDesc: "NLP sentiment analysis aggregated across global news sources (24h)",
    sentimentSources: "sources",
    sentimentBullish: "Bullish Sentiment:",
    sentimentBull: "Bull",
    sentimentNeut: "Neut",
    sentimentBear: "Bear",
    sentimentBullishLabel: "Bullish",
    sentimentBearishLabel: "Bearish",
    sentimentNeutralLabel: "Neutral",
    // Email Import Panel
    emailImportTitle: "Email Auto-Import sync",
    imapUsername: "IMAP Username",
    imapPassword: "IMAP App Password",
    updateCredentials: "Update credentials",
    syncNow: "Sync Mailbox Now",
    saving: "Saving...",
    syncingMailbox: "Syncing Mailbox...",
    emailSaved: "Email credential configuration saved!",
    imapCompleted: "IMAP mailbox crawl sync completed successfully!",
    // Alerts Panel
    alertsTitle: "Price Target Alerts",
    assetPlaceholder: "Asset...",
    above: "Above",
    below: "Below",
    triggerPrice: "Trigger Price",
    addBtn: "Add",
    noAlerts: "No active price alerts set",
    triggered: "Triggered",
    deleteBtn: "Delete",
    chooseAssetErr: "Please choose asset and threshold price",
    // Forecaster Panel
    forecasterTitle: "Horizon Investment Forecaster",
    forecasterDesc: "Multi-horizon price projection and confidence boundaries",
    selectTickerLabel: "SELECT TICKER",
    principalLabel: "INVESTMENT PRINCIPAL (€)",
    annualDriftLabel: "ESTIMATED ANNUAL GROWTH (DRIFT)",
    annualVolLabel: "HISTORICAL ANNUALIZED VOLATILITY",
    pctHighLabel: "90% Bound",
    pctLowLabel: "10% Bound",
    expectedReturnLabel: "expected",
    horizon7d: "7 Days",
    horizon14d: "14 Days",
    horizon1m: "1 Month",
    horizon6m: "6 Months",
    // Trade Form
    tradeExecutionTicket: "Trade Execution Ticket",
    buyAsset: "Buy Asset",
    sellAsset: "Sell Asset",
    buyLabel: "BUY",
    sellLabel: "SELL",
    selectTicker: "Select Ticker",
    chooseAsset: "Choose asset...",
    quantity: "Quantity",
    limitPrice: "Limit Price",
    estimatedTotalCost: "Estimated Total Cost",
    feeLabel: "Fee (€)",
    authorizeExecution: "Authorize Execution",
    processing: "Processing...",
    pleaseFillAllFields: "Please fill in all fields",
    buyProcessed: "Buy transaction processed!",
    sellProcessed: "Sell transaction processed!",
    transactionFailed: "Transaction failed",
    addNewTickerHeader: "Add new ticker (Global Markets)",
    symbolLabel: "Symbol (e.g. CSG.AS, BMW.DE, AAPL)",
    companyName: "Company Name",
    currency: "Currency",
    exchange: "Exchange",
    sector: "Sector",
    saveTickerBtn: "Save new ticker to directory",
    addTickerBtn: "➕ Add Ticker",
    backBtn: "✕ Back",
    // Transaction Log
    transactionLogTitle: "Transaction & Upload Log",
    transactionLogDesc: "Audit log of statement imports & manual orders",
    refreshLogBtn: "REFRESH LOG",
    colDateTime: "Date & Time",
    colType: "Type",
    colFee: "Fee",
    colSource: "Source",
    loadingAudit: "Loading audit history...",
    noTransactions: "No transactions recorded.",
    showing: "Showing",
    of: "of",
    items: "items",
    prevBtn: "PREV",
    nextBtn: "NEXT",
    statementSync: "statement_sync",
    manualEntry: "manual_entry",
    // Table & Hub additions
    colAvgWithFee: "Avg + Fee",
    colTotalCost: "Total Cost",
    colCurrentPrice: "Current Price",
    colMarketVal: "Market Value",
    colWeight: "Weight (% AUM)",
    colNetPl: "Net P&L",
    viewTable: "Table",
    viewGrid: "Grid Preview",
    activePositionsTitle: "Active Positions",
    activePositionsDesc: "Portfolio table of your current open investment holdings",
    searchPositionPlaceholder: "Search position (symbol, name)...",
    noMatchingPositions: "No matching positions found",
    openCandlestickChart: "Open Candlestick Chart",
    costBasisWithFee: "Cost Basis (incl. fees)",
    netProfitLoss: "Net Profit / Loss (P/L)",
    orderBookTitle: "Pending Orders & Order Book",
    orderBookDesc: "Manage and authorize trade tickets before execution",
    estTotal: "Est. Total",
    statusCol: "Status",
    actionCol: "Action",
    pendingStatus: "PENDING",
    filledStatus: "FILLED",
    cancelledStatus: "CANCELLED",
    confirmFill: "Confirm Fill",
    cancelOrder: "Cancel",
    confirmMarketExecution: "Confirm Market Execution",
    instrument: "Instrument:",
    orderQuantity: "Quantity:",
    orderNote: "Order Note:",
    actualFillPrice: "Actual Fill Price",
    brokerExchangeFee: "Broker / Exchange Fee",
    ticketReference: "Execution note / Ticket reference",
    bookExecution: "Book Execution",
    cancelBtn: "Cancel",
    totalFeesPaid: "Total Fees Paid",
    freeCashTitle: "Free Cash",
    freeCashTooltip: "Click to manage free cash balances",
    cashModalTitle: "Free Cash Management (Cash Vault)",
    cashModalDesc: "Current liquidity balances and manual adjustment of deposits / withdrawals",
    cashModalAdjust: "Manual Cash Adjustment / Movement",
    cashModalCurrency: "Currency",
    cashModalOp: "Operation Type",
    cashModalAmount: "Amount",
    cashModalSet: "Set Exact Balance",
    cashModalDeposit: "Deposit (+)",
    cashModalWithdraw: "Withdrawal (-)",
    cashModalSave: "Save Balance",
    cashModalSuccess: "Cash balance updated successfully!",
    targetPlGoal: "Target P/L Goal",
    targetPlModalTitle: "Set Target P/L Performance Benchmark",
    targetPlModalDesc: "Customize your expected annual portfolio return percentage or fixed cash gain",
    targetTypePercent: "% Annual Return (p.a.)",
    targetTypeFixed: "💶 Fixed Gain in EUR",
    targetSaveSuccess: "Target P/L goal successfully updated!",
    sectorBreakdownTitle: "Sector Breakdown & Asset Inspector",
    sectorBreakdownDesc: "Real economic sector weights, currency exposure (EUR/USD) and interactive asset inspector",
    concentrationAlert: "Concentration alert",
    portfolioAssetsClick: "Portfolio assets (click to inspect):",
    openChartAndHistory: "Open Chart & History",
    tradeAssetBtn: "Trade Asset",
    diversificationStatus: "Allocation Status",
    highConcentration: "⚠️ OVERWEIGHT",
    underweight: "🌱 UNDERWEIGHT",
    balancedWeight: "✓ BALANCED",
    considerRebalancing: "Consider rebalancing",
    roomToAccumulate: "Room to accumulate",
    subtabOverview: "Overview",
    subtabBuys: "Transaction Ledger",
    subtabPrices: "Price Delta",
    subtabCalendar: "Event Calendar",
    subtabExports: "Exports",
    csvExportTitle: "CSV Ledger Exports",
    csvExportDesc: "Download complete structured raw text ledger files for spreadsheet audits",
    exportToCsvBtn: "Export to CSV",
    filterAllSectors: "All Sectors",
    filterAllCurrencies: "All Currencies",
    filterGainersOnly: "Gainers (+)",
    filterLosersOnly: "Losers (-)",
    clearFilters: "Clear",
    showingResults: "Showing",
    ofPositions: "positions",
    navPortfolio: "Portfolio",
    navCharts: "Market & Charts",
    navSentiment: "News & Sentiment",
    navQuant: "Quant & Risk",
    navWealth: "Wealth & Tax",
    navSettings: "Order Entry",
    navAdmin: "System Admin",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar",
    actionTrade: "Trade",
    clickToChart: "Click to open interactive chart",
    quickBuy: "Buy",
    colActions: "Actions",
    lastUpdated: "Updated",
    justNow: "just now",
    secondsAgo: "s ago",
    minutesAgoText: "m ago",
    syncData: "Sync data",
    marketOpen: "US MARKETS OPEN",
    marketClosed: "US MARKETS CLOSED",
    afterHours: "AFTER HOURS",
    notificationCenter: "Notification Center",
    markAllRead: "Mark all as read",
    noNotifications: "No unread notifications",
    orderNotification: "Order Alert",
    systemNotification: "System Telemetry",
    priceNotification: "Price Alert",
    viewAllOrders: "View all orders in Order Book",
    tgOptionNewOrders: "Instant alerts on new Buy/Sell orders",
    tgOptionExecuted: "Notification when order is executed & settled",
    tgOptionPriceAlerts: "Trigger alerts when stock crosses limit",
    tgOptionMorningBrief: "Daily morning portfolio brief at 08:30 CET",
    soundFeedbackTitle: "Audio Feedback & Alerts",
    soundFeedbackDesc: "Discreet institutional chime for incoming orders and limit breaches",
    soundFeedbackToggle: "Sound Notifications",
    soundTestDing: "Play Sample Ding",
    hotkeySearchHint: "Press / to Search Assets",
    hotkeyEscHint: "Press Esc to Close",
    loginTitle: "Sign in to Anogi Traders",
    loginSubtitle: "Enter your credentials to access your trading dashboard",
    loginEmail: "Email / Username",
    loginPassword: "Password",
    loginSubmit: "Sign In",
    loginSubmitting: "Signing in...",
    loginQuickBtn: "1-Click Quick Admin Sign In",
    loginOrManual: "or sign in manually",
    loginNewUser: "New to the platform?",
    loginCreateAccount: "Create an Account",
    loginInvalidCreds: "Invalid credentials",
    loginAutoSignInFailed: "Quick login failed",
  },
  sk: {
    overview: "Prehľad & Obchodovanie",
    tools: "Pokročilé Nástroje",
    settings: "Nastavenia & Objednávky",
    admin: "Administrácia",
    logout: "Odhlásiť sa",
    statsValuation: "Aktuálna Trhová Hodnota",
    statsInvested: "Investované Prostriedky",
    statsNetProfit: "Aktuálny Výnos",
    statsRealized: "Realizovaný",
    statsSharpe: "Sharpeho Pomer",
    statsSharpeDesc: "Výnos upravený o riziko",
    statsVar: "Hodnota v Riziku (VaR 95%)",
    statsMaxDd: "Max Pokles",
    weightTitle: "Prieskumník Váh Alokácie Aktív",
    weightDesc: "Kliknutím na aktívum zameriate kvantitatívne modely rizika a projekcie",
    unitsAt: "kusov @",
    loadingMetrics: "Načítavam analytické metriky...",
    // holdings table
    holdingsTitle: "Aktívne Pozície",
    colAsset: "Aktívum",
    colQty: "Množstvo",
    colAvgCost: "Priem. Cena",
    colPrice: "Trhová Cena",
    colVal: "Hodnota",
    colGainLoss: "Celkový Zisk/Strata",
    noHoldings: "Žiadne aktívne pozície. Nakúpte aktíva pre sledovanie.",
    // Newly added
    liveFeed: "Živý prenos",
    offline: "Offline",
    explanationBtn: "Návod k aplikácii",
    explanationTitle: "Návod k aplikácii & Inštalácia",
    explanationDesc: "Používateľská príručka a postup inštalácie na iPhone (iOS) a Android",
    closeBtn: "Zavrieť",
    understoodBtn: "Rozumiem",
    viewPie: "Koláčový graf",
    viewPieByAsset: "Podľa titulov",
    viewPieBySector: "Podľa sektorov",
    // 5 Dashboard components
    rebalanceTitle: "⚖️ Poradca pre Rebalansovanie",
    rebalanceDesc: "Akčné nákupné a predajné odporúčania pre cieľovú alokáciu",
    rebalanceEmpty: "✓ Portfólio je vyvážené. Všetky pozície sú v rozmedzí 1.5% od cieľových váh.",
    rebalanceUnits: "kusov",
    frontierTitle: "📈 Graf Efektívnej Hranice",
    frontierDesc: "Teória moderného portfólia: Profil rizika (volatility) a očakávaného výnosu",
    frontierPortfolio: "PORTFÓLIO",
    harvestTitle: "💸 Sledovanie Daňových Strát",
    harvestDesc: "Skenovanie pozícií v strate na zníženie daňovej povinnosti (odhad 19% sadzby)",
    harvestEmpty: "✓ Neboli nájdené žiadne príležitosti na realizáciu stratu. Všetky pozície sú v zisku alebo majú minimálnu stratu.",
    taxCreditLabel: "Odhadovaný daňový kredit k dispozícii:",
    harvestDetail: "kusov nakúpených",
    harvestLoss: "Strata",
    harvestTaxSave: "Úspora",
    stressTitle: "🌪️ Simulátor Makro Záťažových Testov",
    stressDesc: "Simulované výnosy pod vplyvom makro záťažových faktorov na záklace váženej citlivosti",
    stressRateSpike: "Zvýšenie úrokových sadzieb (+100 bodov)",
    stressRateSpikeDesc: "Rýchle zvyšovanie sadzieb centrálnou bankou zvyšujúce náklady na dlhový kapitál.",
    stressInflation: "Inflačný šok (+3% jadrová)",
    stressInflationDesc: "Náhle zvýšenie cien komodít a vstupných cien.",
    stressPanic: "Globálna finančná panika (VIX 40+)",
    stressPanicDesc: "Systémová likvidácia rizikových aktív naprieč akciovými klastrami.",
    sentimentTitle: "📰 Sentiment Index & Správy",
    sentimentDesc: "NLP analýza sentimentu agregovaná z globálnych správ (24h)",
    sentimentSources: "zdrojov",
    sentimentBullish: "Býčí sentiment:",
    sentimentBull: "Býk",
    sentimentNeut: "Neutr",
    sentimentBear: "Medveď",
    sentimentBullishLabel: "Býčí",
    sentimentBearishLabel: "Medvedí",
    sentimentNeutralLabel: "Neutrálny",
    // Email Import Panel
    emailImportTitle: "Automatický import z e-mailov",
    imapUsername: "Prihlasovacie meno IMAP",
    imapPassword: "Heslo aplikácie IMAP",
    updateCredentials: "Aktualizovať prihlasovacie údaje",
    syncNow: "Synchronizovať e-mailovú schránku",
    saving: "Ukladám...",
    syncingMailbox: "Synchronizujem schránku...",
    emailSaved: "Prihlasovacie údaje k e-mailu boli uložené!",
    imapCompleted: "Automatická synchronizácia e-mailov prebehla úspešne!",
    // Alerts Panel
    alertsTitle: "Cenové upozornenia",
    assetPlaceholder: "Aktívum...",
    above: "Nad",
    below: "Pod",
    triggerPrice: "Aktivačná cena",
    addBtn: "Pridať",
    noAlerts: "Nie sú nastavené žiadne aktívne upozornenia",
    triggered: "Aktivované",
    deleteBtn: "Vymazať",
    chooseAssetErr: "Prosím zvoľte aktívum a hraničnú cenu",
    // Forecaster Panel
    forecasterTitle: "Predpovedný model investícií",
    forecasterDesc: "Viacúrovňová projekcia cien a hranice spoľahlivosti",
    selectTickerLabel: "ZVOLIŤ TICKER",
    principalLabel: "INVESTIČNÁ ISTINA (€)",
    annualDriftLabel: "ODHADOVANÝ ROČNÝ RAST (DRIFT)",
    annualVolLabel: "HISTORICKÁ ROČNÁ VOLATILITA",
    pctHighLabel: "90% Hranica",
    pctLowLabel: "10% Hranica",
    expectedReturnLabel: "očakávané",
    horizon7d: "7 dní",
    horizon14d: "14 dní",
    horizon1m: "1 mesiac",
    horizon6m: "6 mesiacov",
    // Trade Form
    tradeExecutionTicket: "Lístok na realizáciu obchodu",
    buyAsset: "Kúpiť aktívum",
    sellAsset: "Predať aktívum",
    buyLabel: "NÁKUP",
    sellLabel: "PREDAJ",
    selectTicker: "Zvoliť ticker",
    chooseAsset: "Vyberte aktívum...",
    quantity: "Množstvo",
    limitPrice: "Limitná cena",
    estimatedTotalCost: "Odhadovaná celková cena",
    feeLabel: "Poplatok (€)",
    authorizeExecution: "Autorizovať realizáciu",
    processing: "Spracovávam...",
    pleaseFillAllFields: "Prosím vyplňte všetky polia",
    buyProcessed: "Nákupná transakcia bola spracovaná!",
    sellProcessed: "Predajná transakcia bola spracovaná!",
    transactionFailed: "Transakcia zlyhala",
    addNewTickerHeader: "Pridať novú akciu (Globálne trhy)",
    symbolLabel: "Symbol (napr. CSG.AS, BMW.DE, AAPL)",
    companyName: "Názov spoločnosti",
    currency: "Mena",
    exchange: "Burza",
    sector: "Sektor",
    saveTickerBtn: "Uložiť novú akciu do zoznamu",
    addTickerBtn: "➕ Pridať akciu",
    backBtn: "✕ Späť",
    // Transaction Log
    transactionLogTitle: "Log transakcií a nahrávania",
    transactionLogDesc: "Auditný log importov výpisov a manuálnych objednávok",
    refreshLogBtn: "OBNOVIŤ LOG",
    colDateTime: "Dátum a čas",
    colType: "Typ",
    colFee: "Poplatok",
    colSource: "Zdroj",
    loadingAudit: "Načítavam auditnú históriu...",
    noTransactions: "Žiadne zaznamenané transakcie.",
    showing: "Zobrazujem",
    of: "z",
    items: "položiek",
    prevBtn: "PREDCH",
    nextBtn: "NASL",
    statementSync: "import_výpisu",
    manualEntry: "manuálny_vstup",
    // Table & Hub additions
    colAvgWithFee: "Nákup + Fee",
    colTotalCost: "Celk. Náklad",
    colCurrentPrice: "Aktuálna Cena",
    colMarketVal: "Trhová Hodnota",
    colWeight: "Váha (% AUM)",
    colNetPl: "Čistý Zisk (P/L)",
    viewTable: "Tabuľka",
    viewGrid: "Vizuálna Mriežka",
    activePositionsTitle: "Aktívne Pozície",
    activePositionsDesc: "Zoznam vašich otvorených investičných pozícií",
    searchPositionPlaceholder: "Hľadať pozíciu (symbol, názov)...",
    noMatchingPositions: "Nenašli sa žiadne vyhovujúce pozície",
    openCandlestickChart: "Otvoriť Sviečkový Graf",
    costBasisWithFee: "Nákupný náklad (istina + fee)",
    netProfitLoss: "Čistý Zisk / Strata (P/L)",
    orderBookTitle: "Kniha Obchodných Pokynov",
    orderBookDesc: "Správa a autorizácia nákupov a predajov pred burzovou realizáciou",
    estTotal: "Odhad. objem",
    statusCol: "Stav",
    actionCol: "Akcia",
    pendingStatus: "ČAKÁ NA EXEKÚCIU",
    filledStatus: "ZREALIZOVANÉ",
    cancelledStatus: "ZRUŠENÉ",
    confirmFill: "Potvrdiť nákup",
    cancelOrder: "Zrušiť",
    confirmMarketExecution: "Potvrdenie Realizácie na Burze",
    instrument: "Inštrument:",
    orderQuantity: "Počet kusov:",
    orderNote: "Poznámka pokynu:",
    actualFillPrice: "Skutočná dosiahnutá cena za 1 ks",
    brokerExchangeFee: "Poplatok brokera / burzy",
    ticketReference: "Poznámka k exekúcii (napr. ID transakcie u brokera)",
    bookExecution: "Zaúčtovať do portfólia",
    cancelBtn: "Zrušiť",
    totalFeesPaid: "Poplatky Maklérovi",
    freeCashTitle: "Voľná Hotovosť",
    freeCashTooltip: "Kliknite pre manuálnu úpravu voľnej hotovosti",
    cashModalTitle: "Správa Voľnej Hotovosti (Cash Vault)",
    cashModalDesc: "Aktuálne zostatky a manuálna úprava vkladov / výberov",
    cashModalAdjust: "Manuálna Úprava / Pohyb Hotovosti",
    cashModalCurrency: "Mena",
    cashModalOp: "Typ operácie",
    cashModalAmount: "Suma",
    cashModalSet: "Nastaviť zostatok",
    cashModalDeposit: "Vklad (+)",
    cashModalWithdraw: "Výber (-)",
    cashModalSave: "Uložiť zmenu",
    cashModalSuccess: "Hotovosť bola úspešne upravená!",
    targetPlGoal: "Cieľový P/L Výnos",
    targetPlModalTitle: "Nastaviť Cieľ Zhodnotenia Portfólia",
    targetPlModalDesc: "Zvoľte si požadovaný ročný percentuálny výnos alebo pevnú cieľovú sumu v EUR",
    targetTypePercent: "% Ročný výnos (p.a.)",
    targetTypeFixed: "💶 Fixný zisk v EUR",
    targetSaveSuccess: "Cieľ zhodnotenia portfólia bol úspešne uložený!",
    sectorBreakdownTitle: "Sektorové Rozloženie & Inšpektor Aktív",
    sectorBreakdownDesc: "Reálne sektorové váhy, menová expozícia (EUR/USD) a detail vybraného aktíva po kliknutí",
    concentrationAlert: "Koncentračné riziko",
    portfolioAssetsClick: "Jednotlivé akcie v portfóliu (kliknite pre detail):",
    openChartAndHistory: "Otvoriť Graf & Históriu",
    tradeAssetBtn: "Kúpiť / Predať",
    diversificationStatus: "Status Diverzifikácie",
    highConcentration: "⚠️ VYSOKÁ KONCENTRÁCIA",
    underweight: "🌱 MINORITNÁ POZÍCIA",
    balancedWeight: "✓ VYVÁŽENÁ VÁHA",
    considerRebalancing: "Zvážte realizáciu ziskov",
    roomToAccumulate: "Priestor na akumuláciu",
    subtabOverview: "Prehľad",
    subtabBuys: "História nákupov",
    subtabPrices: "Zmeny Cien",
    subtabCalendar: "Kalendár",
    subtabExports: "Exporty",
    csvExportTitle: "Export CSV Súboru",
    csvExportDesc: "Stiahnite si surové transakčné údaje pre tabuľkové procesory",
    exportToCsvBtn: "Exportovať do CSV",
    filterAllSectors: "Všetky sektory",
    filterAllCurrencies: "Všetky meny",
    filterGainersOnly: "Ziskové (+)",
    filterLosersOnly: "Stratové (-)",
    clearFilters: "Reset",
    showingResults: "Zobrazených",
    ofPositions: "pozícií",
    navPortfolio: "Portfólio",
    navCharts: "Trh & Grafy",
    navSentiment: "Správy & Sentiment",
    navQuant: "Kvant & Riziko",
    navWealth: "Majetok & Dane",
    navSettings: "Nové Pokyny",
    navAdmin: "Nastavenia & Admin",
    collapseSidebar: "Zasunúť menu",
    expandSidebar: "Rozbaliť menu",
    actionTrade: "Obchodovať",
    clickToChart: "Kliknutím otvoríte interaktívny sviečkový graf",
    quickSell: "Predať",
    colActions: "Akcie",
    lastUpdated: "Aktualizované",
    justNow: "práve teraz",
    secondsAgo: "s dozadu",
    minutesAgoText: "m dozadu",
    syncData: "Synchronizovať dáta",
    marketOpen: "US TRHY OTVORENÉ",
    marketClosed: "US TRHY ZATVORENÉ",
    afterHours: "POOBCHODNÉ HODINY",
    notificationCenter: "Centrum Notifikácií & Správ",
    markAllRead: "Označiť ako prečítané",
    noNotifications: "Žiadne nové neprečítané správy",
    orderNotification: "Obchodný Pokyn",
    systemNotification: "Systémová Telemetria",
    priceNotification: "Cenové Upozornenie",
    viewAllOrders: "Zobraziť všetky pokyny v Knihe pokynov",
    tgOptionNewOrders: "Okamžité push správy pri novom nákupnom/predajnom pokyne",
    tgOptionExecuted: "Potvrdenie po zrealizovaní a zaúčtovaní pokynu",
    tgOptionPriceAlerts: "Upozornenia pri prelomení cenových limitov akcií",
    tgOptionMorningBrief: "Denný ranný prehľad portfólia o 08:30 CET",
    soundFeedbackTitle: "Zvuková Odozva & Upozornenia",
    soundFeedbackDesc: "Diskrétny inštitucionálny 'ding' pri príchode nového pokynu alebo cenovom alerte",
    soundFeedbackToggle: "Zvukové Upozornenia",
    soundTestDing: "Prehrať testovací Ding",
    hotkeySearchHint: "Stlačte / pre vyhľadávanie aktív",
    hotkeyEscHint: "Stlačte Esc pre zatvorenie okna",
    loginTitle: "Prihlásenie do Anogi Traders",
    loginSubtitle: "Zadajte svoje prihlasovacie údaje pre prístup k platforme",
    loginEmail: "Email / Používateľské meno",
    loginPassword: "Heslo",
    loginSubmit: "Prihlásiť sa",
    loginSubmitting: "Prihlasovanie...",
    loginQuickBtn: "1-Klik Rýchle Prihlásenie Správcu",
    loginOrManual: "alebo prihlásenie heslom",
    loginNewUser: "Nový používateľ?",
    loginCreateAccount: "Vytvoriť Účet",
    loginInvalidCreds: "Nesprávny email alebo heslo",
    loginAutoSignInFailed: "Rýchle prihlásenie zlyhalo",
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'sk' || saved === 'en') ? saved : 'en';
  });

  const setLanguage = (lang: Language) => {
    localStorage.setItem('language', lang);
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
